"use client";

import { CheckIcon } from "../CheckIcon";
import { FeatureList } from "../FeatureList";
import { FlowSelect } from "../FlowSelect";
import { ReviewsRow } from "../ReviewsRow";
import { PlanCard } from "../PlanCard";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { SHAREHOLDER_OPTIONS } from "../options";
import { PLANS, type PackageSummary } from "../pricing";

const DUTIES = [
  "Maintains company records",
  "Handles statutory filings",
  "Keeps your company compliant",
];

const INCLUDED = [
  "HK Company Registration",
  "PayPal with VIP Support (6% dispute tolerance)",
  "Payoneer VIP Account and 5+ more banks",
  "Airwallex Account",

  "Access to 9+ PayPal Reps",
  "Unlimited PayPal Reactivations",
  "Stripe",
];

const INCLUDED_ADVANCED = [
  "Change of company name (government fee excluded)",
  "Allotment of Shares",
  "Transfer of Shares",
  "Change of director (appointment/cessation)",
];


export function CompanySecretaryStep({
  shareholders,
  onShareholdersChange,
  plan,
  onPlanChange,
  summary,
  onNext,
}: {
  shareholders: string;
  onShareholdersChange: (value: string) => void;
  plan: string | null;
  onPlanChange: (value: string) => void;
  summary: PackageSummary;
  onNext: () => void;
}) {
  // The advanced list is premium-only, so it reframes itself against the
  // current choice. Name read from the plan list so renaming a tier can't leave
  // the upsell copy pointing at a plan that no longer exists.
  const premiumName = PLANS.find((p) => p.id === "premium")?.name ?? "Premium";
  const advancedIncluded = plan === "premium";
  const advancedLocked = plan === "standard";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (plan) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* Left column. items-end only from lg — below that it would push the
            non-stretching children (the reviews row, the Next button) to the
            right while everything else stays left. */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8 lg:items-end">
          {/* 26px on phones: this heading is ~48 characters, so 36px ran to
              five lines and filled the screen before any content. */}
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[71px]">
            Every Hong Kong company needs a Company Secretary
          </h1>

          <div className="w-full">
            <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
              We help you stay compliant with annual filings, company updates, and government
              requirements.
            </p>
            <h2 className="mt-5 font-suisse-intl-bold text-xl leading-snug text-white sm:mt-[28.8px] sm:text-2xl sm:leading-[38.4px]">
              What does a Company Secretary do?
            </h2>
          </div>

          <ul className="flex w-full flex-col items-start gap-2">
            {DUTIES.map((duty) => (
              <li key={duty} className="flex w-full items-start gap-2">
                <CheckIcon />
                <span className="font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                  {duty}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex w-full lg:items-end lg:justify-end">
            <ReviewsRow />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            <label
              htmlFor="shareholders"
              className="font-suisse-intl-medium text-base leading-snug text-white sm:text-lg sm:leading-4"
            >
              How many shareholders do you have?
            </label>
            <FlowSelect
              id="shareholders"
              value={shareholders}
              onChange={onShareholdersChange}
              options={SHAREHOLDER_OPTIONS.map((option) => ({ value: option, label: option }))}
            />
          </div>

          <div className="flex w-full flex-col items-center gap-6 sm:gap-8">
            <h2 className="w-full font-suisse-works text-[28px] leading-tight text-white [text-shadow:0px_4px_24px_rgba(0,0,0,0.05)] sm:text-[40px] sm:leading-[64px] lg:text-5xl">
              Choose a plan
            </h2>

            {/* Side by side only from lg: two cards at 640px leave ~230px of
                content each, which the struck-through-plus-current price pair
                overruns. */}
            <div className="flex w-full flex-col gap-6 sm:gap-8 lg:flex-row lg:items-stretch">
              {PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} selected={plan === p.id} onSelect={onPlanChange} />
              ))}
            </div>

            <div className="flex w-full flex-col gap-6 sm:flex-row">
              <FeatureList title="What's included" items={INCLUDED} />
              <FeatureList
                title={
                  advancedLocked ? `Upgrade to ${premiumName} for these` : "Included (Advanced)"
                }
                items={INCLUDED_ADVANCED}
                tone={advancedIncluded ? "included" : advancedLocked ? "locked" : "default"}
              />
            </div>
          </div>

          {/* Full width on phones; the design's 188px right-aligned pill from
              lg, where items-end applies. */}
          <button
            type="submit"
            disabled={!plan}
            className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 lg:max-w-[188px]"
          >
            Next
          </button>
        </div>

        {/* Right column — order summary */}
        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
